import * as THREE from 'three';

export default class GasStation {
    // Static property to track visited stations
    static lastVisitedStation = null;
    
    // Static method to handle when a new station is visited
    static handleNewVisit(station) {
        // If there was a previous station, it's now available again
        if (GasStation.lastVisitedStation && GasStation.lastVisitedStation !== station) {
            // Previous station remains marked as used, but is no longer the last visited
            // The color indicator on the minimap will show this difference
        }
        // Set this station as the last visited
        GasStation.lastVisitedStation = station;
    }
    
    constructor(scene, physics, position = { x: 0, z: 0 }) {
        this.scene = scene;
        this.physics = physics;
        this.position = position;
        
        // Gas station properties
        this.name = this.generateName();
        this.active = true;
        this.refuelDistance = 15; // Distance in units where refueling is possible
        this.refuelRate = 10; // Fuel units per second
        this.fuelPrice = 1.0 + Math.random() * 0.5; // Price per unit, randomized
        this.hasBeenUsed = false; // Track if this station has been used for refueling
        
        // Meshes and objects
        this.mainBuilding = null;
        this.canopy = null;
        this.pumps = [];
        this.colliders = [];
        this.markerMesh = null;
        
        // Refueling state
        this.isRefueling = false;
        this.nearbyBus = null;
        
        // Create the gas station
        this.create();
        
        // Add minimap marker
        this.createMinimapMarker();
    }
    
    // Generate a random gas station name
    generateName() {
        const prefixes = ['Desert', 'Roadside', 'Oasis', 'Canyon', 'Highway', 'Horizon', 'Dusty', 'Sunny'];
        const names = ['Fuel Stop', 'Gas n Go', 'Petrol Point', 'Fuel Depot', 'Oil & Gas', 'Refuel Station'];
        
        const prefix = prefixes[Math.floor(Math.random() * prefixes.length)];
        const name = names[Math.floor(Math.random() * names.length)];
        
        return `${prefix} ${name}`;
    }
    
    create() {
        // Create a group for all gas station parts
        this.group = new THREE.Group();
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap ? heightMap.getHeight(this.position.x, this.position.z) : 0;
        this.group.position.set(this.position.x, terrainY, this.position.z);
        
        // Rotate randomly for variety
        this.group.rotation.y = Math.random() * Math.PI * 2;
        
        // Create main building
        this.createMainBuilding();
        
        // Create canopy and pumps
        this.createCanopy();
        
        // Create fuel pumps
        this.createPumps();
        
        // Add the entire group to the scene
        this.scene.add(this.group);
        
        // Create physics colliders
        this.createPhysicsColliders();
    }
    
    createMainBuilding() {
        // Dimensions
        const width = 12;
        const height = 5;
        const depth = 8;
        
        // Main building
        const buildingGeometry = new THREE.BoxGeometry(width, height, depth);
        
        // Create materials with different colors for each side
        const materials = [
            new THREE.MeshStandardMaterial({ color: 0xDDDDDD, roughness: 0.6 }), // right
            new THREE.MeshStandardMaterial({ color: 0xDDDDDD, roughness: 0.6 }), // left
            new THREE.MeshStandardMaterial({ color: 0x888888, roughness: 0.7 }), // top
            new THREE.MeshStandardMaterial({ color: 0xCCCCCC, roughness: 0.8 }), // bottom
            new THREE.MeshStandardMaterial({ color: 0xDDDDDD, roughness: 0.6 }), // front
            new THREE.MeshStandardMaterial({ color: 0xDDDDDD, roughness: 0.6 })  // back
        ];
        
        this.mainBuilding = new THREE.Mesh(buildingGeometry, materials);
        this.mainBuilding.position.set(0, height / 2, -depth);
        this.mainBuilding.castShadow = true;
        this.mainBuilding.receiveShadow = true;
        this.group.add(this.mainBuilding);
        
        // Add windows
        this.addWindows(width, height, depth);
        
        // Add sign at the top
        this.createSign(width, height, depth);
    }
    
    addWindows(buildingWidth, buildingHeight, buildingDepth) {
        // Front windows
        const windowWidth = 2.5;
        const windowHeight = 2;
        const frontWindowGeometry = new THREE.PlaneGeometry(windowWidth, windowHeight);
        const windowMaterial = new THREE.MeshStandardMaterial({
            color: 0x88CCFF,
            transparent: true,
            opacity: 0.7,
            metalness: 0.2,
            roughness: 0.1,
            side: THREE.DoubleSide
        });
        
        // Create front windows
        const frontWindowPositions = [
            { x: -buildingWidth / 4, y: 0, z: buildingDepth / 2 + 0.05 },
            { x: buildingWidth / 4, y: 0, z: buildingDepth / 2 + 0.05 }
        ];
        
        frontWindowPositions.forEach(pos => {
            const frontWindow = new THREE.Mesh(frontWindowGeometry, windowMaterial);
            frontWindow.position.set(
                pos.x, 
                pos.y + buildingHeight / 3, 
                pos.z - buildingDepth
            );
            this.group.add(frontWindow);
        });
        
        // Create door
        const doorGeometry = new THREE.PlaneGeometry(2, 3);
        const doorMaterial = new THREE.MeshStandardMaterial({
            color: 0x333333,
            roughness: 0.5,
            metalness: 0.2,
            side: THREE.DoubleSide
        });
        
        const door = new THREE.Mesh(doorGeometry, doorMaterial);
        door.position.set(0, 1.5, buildingDepth / 2 + 0.05 - buildingDepth);
        this.group.add(door);
    }
    
    createSign(buildingWidth, buildingHeight, buildingDepth) {
        // Create sign at the top of the building
        const signWidth = buildingWidth * 0.8;
        const signHeight = 2;
        const signDepth = 0.5;
        
        const signGeometry = new THREE.BoxGeometry(signWidth, signHeight, signDepth);
        
        // Pick a random sign color scheme
        const colors = [
            { base: 0xFF0000, text: 0xFFFFFF }, // Red with white text
            { base: 0x0000FF, text: 0xFFFF00 }, // Blue with yellow text
            { base: 0x00CC00, text: 0xFFFFFF }  // Green with white text
        ];
        
        const colorScheme = colors[Math.floor(Math.random() * colors.length)];
        
        const signMaterial = new THREE.MeshStandardMaterial({
            color: colorScheme.base,
            roughness: 0.5,
            metalness: 0.3
        });
        
        const sign = new THREE.Mesh(signGeometry, signMaterial);
        sign.position.set(0, buildingHeight + signHeight / 2, -buildingDepth);
        sign.castShadow = true;
        this.group.add(sign);
    }
    
    createCanopy() {
        // Canopy dimensions
        const canopyWidth = 18;
        const canopyHeight = 0.5;
        const canopyDepth = 16;
        const canopyY = 6;
        
        // Canopy top
        const canopyGeometry = new THREE.BoxGeometry(canopyWidth, canopyHeight, canopyDepth);
        const canopyMaterial = new THREE.MeshStandardMaterial({
            color: 0xEEEEEE,
            roughness: 0.5,
            metalness: 0.2
        });
        
        this.canopy = new THREE.Mesh(canopyGeometry, canopyMaterial);
        this.canopy.position.set(0, canopyY, 8);
        this.canopy.castShadow = true;
        this.canopy.receiveShadow = true;
        this.group.add(this.canopy);
        
        // Create pillars to support the canopy
        const pillarGeometry = new THREE.CylinderGeometry(0.5, 0.5, canopyY, 8);
        const pillarMaterial = new THREE.MeshStandardMaterial({
            color: 0xCCCCCC,
            roughness: 0.6,
            metalness: 0.3
        });
        
        // Positions for the four pillars
        const pillarPositions = [
            { x: -canopyWidth / 2 + 1, z: -canopyDepth / 2 + 1 },
            { x: canopyWidth / 2 - 1, z: -canopyDepth / 2 + 1 },
            { x: -canopyWidth / 2 + 1, z: canopyDepth / 2 - 1 },
            { x: canopyWidth / 2 - 1, z: canopyDepth / 2 - 1 }
        ];
        
        pillarPositions.forEach(pos => {
            const pillar = new THREE.Mesh(pillarGeometry, pillarMaterial);
            pillar.position.set(pos.x, canopyY / 2, pos.z + 8);
            pillar.castShadow = true;
            pillar.receiveShadow = true;
            this.group.add(pillar);
        });
        
        // Add accent strip around canopy edge
        const stripGeometry = new THREE.BoxGeometry(canopyWidth + 0.5, canopyHeight * 2, 1);
        
        // Pick a random color scheme to match the sign
        const colors = [0xFF0000, 0x0000FF, 0x00CC00];
        const stripColor = colors[Math.floor(Math.random() * colors.length)];
        
        const stripMaterial = new THREE.MeshStandardMaterial({
            color: stripColor,
            roughness: 0.5,
            metalness: 0.3
        });
        
        // Create strips for each side of the canopy
        const strips = [
            { position: { x: 0, y: canopyY + canopyHeight * 0.5, z: 8 + canopyDepth / 2 }, rotation: { x: 0, y: 0, z: 0 } },
            { position: { x: 0, y: canopyY + canopyHeight * 0.5, z: 8 - canopyDepth / 2 }, rotation: { x: 0, y: 0, z: 0 } },
            { position: { x: canopyWidth / 2, y: canopyY + canopyHeight * 0.5, z: 8 }, rotation: { x: 0, y: Math.PI / 2, z: 0 } },
            { position: { x: -canopyWidth / 2, y: canopyY + canopyHeight * 0.5, z: 8 }, rotation: { x: 0, y: Math.PI / 2, z: 0 } }
        ];
        
        strips.forEach(strip => {
            const mesh = new THREE.Mesh(stripGeometry, stripMaterial);
            mesh.position.set(strip.position.x, strip.position.y, strip.position.z);
            mesh.rotation.set(strip.rotation.x, strip.rotation.y, strip.rotation.z);
            this.group.add(mesh);
        });
    }
    
    createPumps() {
        // Create fuel pumps under the canopy
        const pumpWidth = 1.5;
        const pumpHeight = 2.8;
        const pumpDepth = 1.2;
        
        const pumpGeometry = new THREE.BoxGeometry(pumpWidth, pumpHeight, pumpDepth);
        const pumpMaterial = new THREE.MeshStandardMaterial({
            color: 0x666666,
            roughness: 0.7,
            metalness: 0.3
        });
        
        // Create 2 rows of pumps
        const pumpRows = 2;
        const pumpsPerRow = 2;
        const rowSpacing = 8;
        
        for (let row = 0; row < pumpRows; row++) {
            for (let col = 0; col < pumpsPerRow; col++) {
                const pump = new THREE.Mesh(pumpGeometry, pumpMaterial);
                pump.position.set(
                    (col * 6) - 3,
                    pumpHeight / 2,
                    (row * rowSpacing) + 4
                );
                pump.castShadow = true;
                pump.receiveShadow = true;
                this.group.add(pump);
                this.pumps.push(pump);
                
                // Add nozzle (simplified as small box)
                const nozzleGeometry = new THREE.BoxGeometry(0.2, 0.2, 0.8);
                const nozzleMaterial = new THREE.MeshStandardMaterial({
                    color: 0x111111,
                    roughness: 0.5,
                    metalness: 0.5
                });
                
                const nozzle = new THREE.Mesh(nozzleGeometry, nozzleMaterial);
                nozzle.position.set(
                    0.8,
                    -0.5,
                    0.6
                );
                pump.add(nozzle);
                
                // Add hose (simplified as cylinder)
                const hoseGeometry = new THREE.CylinderGeometry(0.1, 0.1, 1, 8);
                const hoseMaterial = new THREE.MeshStandardMaterial({
                    color: 0x222222,
                    roughness: 0.8,
                    metalness: 0.2
                });
                
                const hose = new THREE.Mesh(hoseGeometry, hoseMaterial);
                hose.position.set(
                    0.8,
                    -0.1,
                    0.3
                );
                hose.rotation.x = Math.PI / 2;
                pump.add(hose);
                
                // Add display screen
                const screenGeometry = new THREE.PlaneGeometry(1, 0.8);
                const screenMaterial = new THREE.MeshStandardMaterial({
                    color: 0x222222,
                    emissive: 0x333333,
                    roughness: 0.5,
                    metalness: 0.5
                });
                
                const screen = new THREE.Mesh(screenGeometry, screenMaterial);
                screen.position.set(0, 0.5, pumpDepth / 2 + 0.01);
                pump.add(screen);
            }
        }
        
        // Add fuel islands / concrete pads
        const islandGeometry = new THREE.BoxGeometry(6, 0.3, 14);
        const islandMaterial = new THREE.MeshStandardMaterial({
            color: 0xAAAAAA,
            roughness: 1.0,
            metalness: 0.0
        });
        
        const island = new THREE.Mesh(islandGeometry, islandMaterial);
        island.position.set(0, 0.15, 8);
        island.receiveShadow = true;
        this.group.add(island);
    }
    
    createPhysicsColliders() {
        if (!this.physics || !this.physics.isInitialized) return;
        
        // Add collider for main building
        if (this.mainBuilding) {
            const buildingSize = new THREE.Vector3(12, 5, 8);
            const buildingPos = new THREE.Vector3(
                this.position.x,
                buildingSize.y / 2,
                this.position.z - buildingSize.z
            ).applyQuaternion(this.group.quaternion);
            
            const buildingCollider = this.physics.createStaticBox(
                buildingPos,
                new THREE.Vector3(buildingSize.x / 2, buildingSize.y / 2, buildingSize.z / 2),
                0.5 // Friction coefficient
            );
            
            this.colliders.push(buildingCollider);
        }
        
        // Add colliders for pumps
        this.pumps.forEach(pump => {
            const worldPos = new THREE.Vector3();
            pump.getWorldPosition(worldPos);
            
            const pumpSize = new THREE.Vector3(1.5, 2.8, 1.2);
            const pumpCollider = this.physics.createStaticBox(
                worldPos,
                new THREE.Vector3(pumpSize.x / 2, pumpSize.y / 2, pumpSize.z / 2),
                0.5 // Friction coefficient
            );
            
            this.colliders.push(pumpCollider);
        });
        
        // Add trigger zone for refueling
        // This is a non-physical collision zone that detects when the bus is nearby
        const refuelZoneSize = new THREE.Vector3(20, 5, 20);
        const refuelZonePos = new THREE.Vector3(
            this.position.x,
            refuelZoneSize.y / 2,
            this.position.z + 8
        );
        
        // Store refuel zone for checking bus proximity
        this.refuelZone = {
            position: refuelZonePos,
            size: refuelZoneSize
        };
    }
    
    createMinimapMarker() {
        // Create gas station marker for minimap
        const markerGeometry = new THREE.CircleGeometry(5, 8);
        const markerMaterial = new THREE.MeshBasicMaterial({
            color: 0x00FF00,
            side: THREE.DoubleSide
        });
        
        this.markerMesh = new THREE.Mesh(markerGeometry, markerMaterial);
        this.markerMesh.rotation.x = -Math.PI / 2; // Make it flat and visible from above
        const markerHeightMap = this.scene?.userData?.terrainHeightMap;
        const markerTerrainY = markerHeightMap ? markerHeightMap.getHeight(this.position.x, this.position.z) : 0;
        this.markerMesh.position.set(this.position.x, markerTerrainY + 5, this.position.z);
        
        // Set to layer 1 for minimap visibility only
        this.markerMesh.layers.set(1);
        
        // Create a simple gas pump icon on top
        const iconGeometry = new THREE.PlaneGeometry(6, 6);
        const iconMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFFFFF,
            side: THREE.DoubleSide,
            transparent: true,
            alphaTest: 0.5
        });
        
        const icon = new THREE.Mesh(iconGeometry, iconMaterial);
        icon.position.set(0, 0.1, 0);
        icon.rotation.x = -Math.PI / 2;
        icon.layers.set(1);
        this.markerMesh.add(icon);
        
        // Add to scene
        this.scene.add(this.markerMesh);
    }
    
    // Check if bus is close enough for refueling
    checkBusProximity(bus) {
        if (!bus || !bus.mesh) return false;
        
        // Get bus position
        const busPosition = bus.mesh.position.clone();
        
        // Calculate the world position of the station's refueling area, accounting for group rotation
        const worldPosition = new THREE.Vector3(0, 0, 8);
        worldPosition.applyQuaternion(this.group.quaternion);
        worldPosition.add(new THREE.Vector3(this.position.x, 0, this.position.z));
        
        // Calculate distance between bus and world position of refueling area
        const distanceToStation = busPosition.distanceTo(worldPosition);
        
        // Check if bus is within refueling distance
        return distanceToStation < this.refuelDistance;
    }
    
    // Start refueling process
    startRefueling(bus) {
        if (!bus || this.isRefueling) return;
        
        // Check if this station can be used for refueling
        if (this.hasBeenUsed && this === GasStation.lastVisitedStation) {
            // Show notification that the station can't be used again
            this.showStationUsedNotification();
            return;
        }
        
        // Check if bus has enough coins
        const refuelCost = window.game ? window.game.refuelCost : 10;
        if (bus.coins < refuelCost) {
            // Show notification that more coins are needed
            this.showNotEnoughCoinsNotification(refuelCost, bus.coins);
            return;
        }
        
        // Deduct coins
        bus.coins -= refuelCost;
        
        
        // Emit event to update UI
        const coinEvent = new CustomEvent('coins_updated', {
            detail: {
                coins: bus.coins,
                operation: 'refuel',
                cost: refuelCost
            }
        });
        window.dispatchEvent(coinEvent);
        
        this.isRefueling = true;
        this.nearbyBus = bus;
        
        // Mark this station as used and update tracking
        this.hasBeenUsed = true;
        GasStation.handleNewVisit(this);
        
        // Show refueling notification
        this.showRefuelingStarted(refuelCost);
    }
    
    // Stop refueling process
    stopRefueling() {
        if (!this.isRefueling) return;
        
        this.isRefueling = false;
        
        // Reset refuel amount tracker
        this.currentRefuelAmount = 0;
        
        if (this.nearbyBus) {
            // Show refueling complete notification
            this.showRefuelingCompleted();
            this.nearbyBus = null;
        }
    }
    
    // Update refueling (called each frame when refueling)
    updateRefueling(deltaTime) {
        if (!this.isRefueling || !this.nearbyBus) return;
        
        // Calculate amount to refuel
        const refuelAmount = this.refuelRate * deltaTime;
        
        // Get 10% of fuel capacity
        const tenPercentCapacity = this.nearbyBus.fuelCapacity * 0.1;
        
        // Track how much fuel we've added during this refueling session
        if (!this.currentRefuelAmount) {
            this.currentRefuelAmount = 0;
        }
        
        // Apply refueling to bus, but limit to 10% of capacity
        if (this.nearbyBus.fuel < this.nearbyBus.fuelCapacity && this.currentRefuelAmount < tenPercentCapacity) {
            // Calculate how much to add this frame
            const amountToAdd = Math.min(
                refuelAmount,
                tenPercentCapacity - this.currentRefuelAmount
            );
            
            // Add fuel
            this.nearbyBus.fuel = Math.min(
                this.nearbyBus.fuel + amountToAdd,
                this.nearbyBus.fuelCapacity
            );
            
            // Track total added
            this.currentRefuelAmount += amountToAdd;
            
            // Restore engine power and input if fuel was previously empty
            if (this.nearbyBus.fuel > 0 && this.nearbyBus.enginePower === 0) {
                this.nearbyBus.enginePower = 300;
                this.nearbyBus.reverseEnginePower = 80;

                // Re-enable player input (isGameOver blocks WASD when fuel hits 0)
                if (this.nearbyBus.inputController) {
                    this.nearbyBus.inputController.isGameOver = false;
                }
            }
            
            // Trigger update to refresh the UI
            const fuelEvent = new CustomEvent('fuel_updated', {
                detail: {
                    level: this.nearbyBus.fuel,
                    capacity: this.nearbyBus.fuelCapacity,
                    percentage: this.nearbyBus.fuel / this.nearbyBus.fuelCapacity * 100,
                    empty: false
                }
            });
            window.dispatchEvent(fuelEvent);
        } else {
            // Stop refueling when we've added 10% or tank is full
            this.stopRefueling();
        }
    }
    
    // Show notification when refueling starts
    showRefuelingStarted(refuelCost) {
        // Create notification element
        const notification = document.createElement('div');
        notification.style.position = 'fixed';
        notification.style.top = '130px';
        notification.style.left = '20px';
        //notification.style.transform = 'translate(-50%, -50%)';
        //notification.style.padding = '15px 30px';
        //notification.style.backgroundColor = 'rgba(0, 100, 180, 0.8)';
        notification.style.color = 'white';
        notification.style.fontFamily = 'Arial, sans-serif';
        notification.style.fontSize = '13px';
        //notification.style.borderRadius = '8px';
        notification.style.zIndex = '100';
        //notification.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
        notification.textContent = `REFUELING AT ${this.name.toUpperCase()}... COST: ${refuelCost} COINS`;
        
        // Add to DOM
        document.body.appendChild(notification);
        
        // Animate in
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s';
        setTimeout(() => {
            notification.style.opacity = '1';
        }, 10);
        
        // Remove after 2 seconds
        setTimeout(() => {
            notification.style.opacity = '0';
            
            // Remove from DOM after fade
            setTimeout(() => {
                if (notification.parentNode) {
                    document.body.removeChild(notification);
                }
            }, 300);
        }, 2000);
    }
    
    // Show notification when refueling is completed
    showRefuelingCompleted() {
        // Create notification element
        const notification = document.createElement('div');
        notification.style.position = 'fixed';
        notification.style.top = '160px';
        notification.style.left = '20px';
        //notification.style.transform = 'translate(-50%, -50%)';
        //notification.style.padding = '15px 30px';
        //notification.style.backgroundColor = 'rgba(0, 180, 0, 0.8)';
        notification.style.color = 'blue';
        notification.style.fontFamily = 'Arial, sans-serif';
        notification.style.fontSize = '15px';
        notification.style.borderRadius = '8px';
        notification.style.zIndex = '100';
        //notification.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
        notification.textContent = 'ADDED 10% FUEL!';
        
        // Add to DOM
        document.body.appendChild(notification);
        
        // Animate in
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s';
        setTimeout(() => {
            notification.style.opacity = '1';
        }, 10);
        
        // Remove after 2 seconds
        setTimeout(() => {
            notification.style.opacity = '0';
            
            // Remove from DOM after fade
            setTimeout(() => {
                if (notification.parentNode) {
                    document.body.removeChild(notification);
                }
            }, 300);
        }, 2000);
    }
    
    // Show notification when the station is used
    showStationUsedNotification() {
        // Create notification element
        const notification = document.createElement('div');
        notification.style.position = 'fixed';
        notification.style.top = '190px';
        notification.style.left = '20px';
        //notification.style.transform = 'translate(-50%, -50%)';
        //notification.style.padding = '15px 30px';
        //notification.style.backgroundColor = 'rgba(180, 0, 0, 0.5)';
        notification.style.color = 'red';
        notification.style.fontFamily = 'Arial, sans-serif';
        notification.style.fontSize = '13px';
        notification.style.borderRadius = '8px';
        notification.style.zIndex = '100';
        //notification.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
        notification.textContent = 'YOU CAN ONLY FILL ONCE PER STATION!';
        
        const notificationAdd = document.createElement('div');
        notificationAdd.style.position = 'fixed';
        notificationAdd.style.top = '210px';
        notificationAdd.style.left = '20px';
        notificationAdd.style.color = 'red';
        notificationAdd.style.fontFamily = 'Arial, sans-serif';
        notificationAdd.style.fontSize = '13px';
        notificationAdd.style.borderRadius = '8px';
        notificationAdd.style.zIndex = '100';
        notificationAdd.textContent = 'VISIT A DIFFERENT STATION FIRST TO FILL HERE AGAIN!';
        // Add to DOM
        document.body.appendChild(notification);
        document.body.appendChild(notificationAdd);
        
        // Animate in
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s';
        notificationAdd.style.opacity = '0';
        notificationAdd.style.transition = 'opacity 0.3s';
        
        setTimeout(() => {
            notification.style.opacity = '1';
            notificationAdd.style.opacity = '1';
        }, 10);
        
        // Remove after 2 seconds
        setTimeout(() => {
            notification.style.opacity = '0';
            notificationAdd.style.opacity = '0';    
            // Remove from DOM after fade
            setTimeout(() => {
                if (notification.parentNode) {
                    document.body.removeChild(notification);
                }
                if (notificationAdd.parentNode) {
                    document.body.removeChild(notificationAdd);
                }
            }, 300);
        }, 2000);
    }
    
    // Show notification when not enough coins are available
    showNotEnoughCoinsNotification(requiredCoins, availableCoins) {
        // Create notification element
        const notification = document.createElement('div');
        notification.style.position = 'fixed';
        notification.style.top = '240px';
        notification.style.left = '20px';
        //notification.style.transform = 'translate(-50%, -50%)';
        //notification.style.padding = '15px 30px';
        notification.style.backgroundColor = 'rgba(180, 0, 0, 0.8)';
        notification.style.color = 'yellow';
        notification.style.fontFamily = 'Arial, sans-serif';
        notification.style.fontSize = '15px';
        //notification.style.borderRadius = '8px';
        notification.style.zIndex = '100';
        notification.style.boxShadow = '0 0 10px rgba(0, 0, 0, 0.3)';
        notification.textContent = `MORE COINS NEEDED! REQUIRED: ${requiredCoins}, AVAILABLE: ${availableCoins}`;
        
        // Add to DOM
        document.body.appendChild(notification);
        
        // Animate in
        notification.style.opacity = '0';
        notification.style.transition = 'opacity 0.3s';
        setTimeout(() => {
            notification.style.opacity = '1';
        }, 10);
        
        // Remove after 2 seconds
        setTimeout(() => {
            notification.style.opacity = '0';
            
            // Remove from DOM after fade
            setTimeout(() => {
                if (notification.parentNode) {
                    document.body.removeChild(notification);
                }
            }, 300);
        }, 2000);
    }
    
    // Remove gas station from scene and physics
    remove() {
        // Remove from scene
        if (this.group) {
            this.scene.remove(this.group);
        }
        
        // Remove minimap marker
        if (this.markerMesh) {
            this.scene.remove(this.markerMesh);
        }
        
        // Remove physics colliders
        if (this.physics && this.colliders) {
            this.colliders.forEach(collider => {
                this.physics.removeCollider(collider);
            });
        }
    }
    
    // Update method called each frame
    update(deltaTime, bus) {
        // Update minimap marker color based on usage state
        if (this.markerMesh) {
            // If this station is the last visited and has been used, make it red
            // Otherwise, if it's been used but not the last one, make it yellow
            // Default is green (unused)
            if (this.hasBeenUsed) {
                if (this === GasStation.lastVisitedStation) {
                    // Make it red if it's the last used station (can't be used again)
                    this.markerMesh.material.color.set(0xFF0000);
                } else {
                    // Make it yellow if it's been used but not the last one
                    this.markerMesh.material.color.set(0xFFFF00);
                }
            } else {
                // Make it green if it hasn't been used
                this.markerMesh.material.color.set(0x00FF00);
            }
        }
        
        // Check if bus is within refueling distance
        if (bus && this.checkBusProximity(bus)) {
            // If the bus is nearby and not currently refueling, start refueling
            if (!this.isRefueling) {
                this.startRefueling(bus);
            }
            
            // Update refueling process
            this.updateRefueling(deltaTime);
        } else if (this.isRefueling) {
            // If the bus moved away, stop refueling
            this.stopRefueling();
        }
    }
}